// French TTS via ElevenLabs (natural voices) with REAL per-character timing for
// the karaoke highlight — a drop-in for the French speak() calls. Falls back to
// the browser voice automatically if anything fails, so playback can't break.
import { speak, IS_IOS } from './speech';
import { reportError } from './analytics';
import { markActive } from './practiceTime';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL || '';

// On for the whole beta. Later this becomes "Pro plan only" once plans exist.
export const TTS_ENABLED = true;

// Repeat plays (loops, replays) shouldn't refetch — cache the audio + timings.
const cache = new Map(); // `${voiceId}|${text}` -> { blobUrl, wordStarts }
let currentAudio = null;

// Said once a session, no more: the natural voice dropping to the device voice
// is silent otherwise. The fallback returns successfully and the word button
// swallows errors, so a paying user hears the wrong voice with nothing to tell
// them why. Free accounts never reach this path, so nobody is nagged about a
// voice they were never promised.
let fallbackWarned = false;

// ── iOS playback unlock ─────────────────────────────────────────────────────
// iOS blocks audio.play() outside a user gesture, and the gesture is spent by
// the time the TTS fetch resolves — so a freshly created Audio() is refused and
// every phrase silently fell back to the browser voice (the iOS French voice is
// male, which is why the chosen voice appeared to "revert"). The fix is the same
// one already used for recorded playback: unlock ONE persistent element inside
// the tap, then reuse it for every phrase. Lives here so the whole app shares a
// single unlocked player instead of each page keeping its own.

// A ~0.05s silent 8-bit mono WAV, built at load, used to unlock the element.
// Built at runtime to avoid a ~600-char base64 literal.
const SILENT_WAV = (() => {
    const sr = 8000, n = 400;
    const bytes = new Uint8Array(44 + n);
    const dv = new DataView(bytes.buffer);
    const w = (o, str) => { for (let i = 0; i < str.length; i++) dv.setUint8(o + i, str.charCodeAt(i)); };
    w(0, 'RIFF'); dv.setUint32(4, 36 + n, true); w(8, 'WAVE'); w(12, 'fmt ');
    dv.setUint32(16, 16, true); dv.setUint16(20, 1, true); dv.setUint16(22, 1, true);
    dv.setUint32(24, sr, true); dv.setUint32(28, sr, true); dv.setUint16(32, 1, true); dv.setUint16(34, 8, true);
    w(36, 'data'); dv.setUint32(40, n, true);
    for (let i = 0; i < n; i++) bytes[44 + i] = 128; // 8-bit PCM silence
    let bin = '';
    for (let i = 0; i < bytes.length; i++) bin += String.fromCharCode(bytes[i]);
    return 'data:audio/wav;base64,' + btoa(bin);
})();

let unlockedPlayer = null;

/**
 * Call from a user gesture (a tap on Start, Play, Preview) before any French
 * audio. No-op off iOS. Safe to call repeatedly.
 */
export function unlockAudioPlayback() {
    if (!IS_IOS) return null;
    try {
        if (!unlockedPlayer) unlockedPlayer = new Audio();
        const el = unlockedPlayer;
        el.src = SILENT_WAV;
        const p = el.play();
        if (p && p.then) p.then(() => { el.pause(); el.currentTime = 0; }).catch(() => {});
    } catch { /* best-effort */ }
    return unlockedPlayer;
}

// The element unlocked above, for other players (recorded audio) to reuse.
export function getUnlockedPlayer() {
    return IS_IOS ? unlockedPlayer : null;
}

// Small delay (seconds) applied to the highlight to counter audio output
// latency (the highlight otherwise runs slightly ahead of what you hear).
// Tunable: raise if it feels early, lower/negative if it feels late.
const HIGHLIGHT_OFFSET = 0.0;

// From ElevenLabs' per-character timings, build one marker per WORD: the char
// index where the word starts and when its first sound begins. Highlighting by
// word (and holding through the spaces between words) removes the flicker you
// get from following every character.
function buildWordStarts(characters, starts) {
    const wordStarts = [];
    for (let i = 0; i < characters.length; i++) {
        const isSpace = /\s/.test(characters[i] || '');
        const prevSpace = i === 0 || /\s/.test(characters[i - 1] || '');
        if (!isSpace && prevSpace) wordStarts.push({ charIndex: i, time: starts[i] ?? 0 });
    }
    return wordStarts;
}

// ── Voice list + selection ──────────────────────────────────────────────────
const SAVED_VOICE_KEY = 'mimique_tts_voice';
let voicesPromise = null; // fetch the offered voices once, then cache

// Both TTS endpoints require a logged-in user — they spend the ElevenLabs
// grant, so they can't be reachable anonymously.
function authHeaders() {
    try {
        const token = localStorage.getItem('token');
        return token ? { Authorization: `Bearer ${token}` } : {};
    } catch {
        return {};
    }
}

// [{ id, name }] of the voices offered in the picker. Empty when TTS is off or
// the plan doesn't include natural voices, which keeps Free on the browser voice.
export function loadTTSVoices() {
    if (!voicesPromise) {
        voicesPromise = fetch(`${API}/api/tts/voices`, { headers: authHeaders() })
            .then(r => (r.ok ? r.json() : { voices: [] }))
            .then(d => Array.isArray(d.voices) ? d.voices : [])
            .catch(() => []);
    }
    return voicesPromise;
}

// Called after login/logout so the next request re-reads the plan's voices.
export function resetTTSVoices() {
    voicesPromise = null;
}

// True when this account's plan includes the natural voices. Free plans get
// none, so asking /api/tts for one is a wasted round trip that can only ever
// come back 402. The picker list is fetched once and cached, so this answers
// without costing a request of its own.
export async function hasNaturalVoices() {
    try {
        return (await loadTTSVoices()).length > 0;
    } catch {
        return false;
    }
}

export function getSavedTTSVoice() {
    try { return localStorage.getItem(SAVED_VOICE_KEY) || ''; } catch { return ''; }
}

export function saveTTSVoice(id) {
    try { localStorage.setItem(SAVED_VOICE_KEY, id || ''); } catch { /* noop */ }
}

function base64ToBlobUrl(b64, mime) {
    const bin = atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return URL.createObjectURL(new Blob([bytes], { type: mime }));
}

async function fetchTTS(text, voiceId, singleWord = false) {
    // The flag is part of the key: a word fetched on its own comes from a
    // different model than the same word inside a phrase, so the two must not
    // share a cache entry.
    const cacheKey = `${voiceId || ''}|${singleWord ? 'w|' : ''}${text}`;
    const cached = cache.has(cacheKey);
    console.log('[TTS]', cached ? 'cache-hit' : 'fetch', 'voice=', voiceId || '(none)', 'text=', text.slice(0, 40));
    if (cached) return cache.get(cacheKey);
    const res = await fetch(`${API}/api/tts`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ text, voice_id: voiceId || undefined, single_word: singleWord }),
    });
    if (!res.ok) {
        const err = new Error(`TTS ${res.status}`);
        err.status = res.status;
        throw err;
    }
    const data = await res.json();
    const chars = data.alignment?.characters || [];
    const starts = data.alignment?.character_start_times_seconds || [];
    const entry = {
        blobUrl: base64ToBlobUrl(data.audio_base64, data.mime || 'audio/mpeg'),
        wordStarts: buildWordStarts(chars, starts),
    };
    cache.set(cacheKey, entry);
    return entry;
}

// Stop any ElevenLabs audio in progress (mirror of speechSynthesis.cancel()).
export function cancelFrench() {
    if (currentAudio) {
        try { currentAudio.pause(); } catch { /* noop */ }
        currentAudio = null;
    }
}

function speakFrenchEL(text, rate, voiceId, onBoundary, singleWord = false, track = true) {
    return fetchTTS(text, voiceId, singleWord).then(({ blobUrl, wordStarts }) => new Promise((resolve, reject) => {
        cancelFrench();
        // Reuse the element unlocked during the tap on iOS — a fresh Audio()
        // would be refused here, the fetch above having outlived the gesture.
        const audio = (IS_IOS && unlockedPlayer) ? unlockedPlayer : new Audio();
        audio.src = blobUrl;
        // After src, which resets it on some engines.
        audio.playbackRate = rate;
        currentAudio = audio;
        let raf = 0;
        let lastWord = -1;
        // Advance to the last word whose start we've reached, and highlight it
        // (holding it through the trailing space until the next word begins).
        // Only fires on word change, so it's cheap and flicker-free.
        const tick = () => {
            const t = audio.currentTime - HIGHLIGHT_OFFSET;
            let w = lastWord;
            while (w + 1 < wordStarts.length && wordStarts[w + 1].time <= t) w++;
            if (w !== lastWord) {
                lastWord = w;
                if (onBoundary && w >= 0) onBoundary(wordStarts[w].charIndex);
            }
            raf = requestAnimationFrame(tick);
        };
        // Practice minutes run only while the French is actually playing.
        let endListen = null;
        audio.onplay = () => {
            raf = requestAnimationFrame(tick);
            if (track && !endListen) endListen = markActive();
        };
        audio.onpause = () => endListen?.();
        audio.onended = () => {
            endListen?.();
            cancelAnimationFrame(raf);
            if (currentAudio === audio) currentAudio = null;
            resolve(audio.duration ? (audio.duration * 1000) / rate : 1500);
        };
        audio.onerror = () => {
            endListen?.();
            cancelAnimationFrame(raf);
            if (currentAudio === audio) currentAudio = null;
            reject(new Error('audio error'));
        };
        audio.play().catch(reject);
    }));
}

/**
 * Drop-in replacement for the French speak() calls:
 * (text, rate, voiceId, onBoundary) -> Promise<ms>. voiceId is the chosen
 * ElevenLabs voice (falls back to the saved one, then the server default).
 * onBoundary(charIndex) drives the karaoke highlight, same as before. Falls
 * back to the browser voice if ElevenLabs fails.
 *
 * `singleWord` is for a word played on its own, away from its sentence. The
 * usual model reads the language off the text, and one bare word does not give
 * it enough: "minutes" came back English, "que" Spanish, "dois" Portuguese.
 * The flag sends it to a model that can be told the language instead.
 */
export async function speakFrench(text, rate = 1.0, voiceId = null, onBoundary = null, singleWord = false, { track = true } = {}) {
    if (TTS_ENABLED && await hasNaturalVoices()) {
        try {
            return await speakFrenchEL(text, rate, voiceId || getSavedTTSVoice(), onBoundary, singleWord, track);
        } catch (e) {
            // The browser voice is a safety net, not a silent substitute: a
            // natural voice dropping to the device voice is exactly the kind of
            // failure that went unnoticed until a tester reported it, so it is
            // reported rather than only logged.
            console.warn('ElevenLabs TTS failed, using browser voice:', e?.message || e);
            // A 402 is the plan speaking, not a fault: this account does not
            // include natural voices, so the browser voice IS the right
            // outcome and there is nothing to report. Only reachable if the
            // plan changed mid-session, so re-read the picker list and the
            // next phrase skips the request altogether.
            if (e?.status === 402) {
                resetTTSVoices();
            } else {
                reportError('tts_natural_voice_fallback', {
                    error_type: 'natural_voice_unavailable',
                    reason: e?.name || e?.message || 'unknown',
                    // The browser's own words, so a repeat can be diagnosed:
                    // "Error" alone (19 Oct 2026) said nothing about the cause.
                    // Saved as the message on the admin dashboard.
                    error: `${e?.name || 'Error'}: ${e?.message || String(e || '')}`.slice(0, 300),
                    http_status: e?.status || null,
                    voice_id: voiceId || getSavedTTSVoice() || '(default)',
                    is_ios: IS_IOS,
                    unlocked: !!unlockedPlayer,
                });
                if (!fallbackWarned) {
                    fallbackWarned = true;
                    toast.error('That played in your device voice, not the natural one. The natural voice is temporarily unavailable.');
                }
            }
        }
    }
    const endListen = track ? markActive() : null;
    try {
        return await speak(text, 'fr-FR', rate, null, onBoundary);
    } finally {
        endListen?.();
    }
}
